import { useEffect, useState } from "react";
import { Col, FormGroup, Input, Label, Row, Table } from "reactstrap";
import Select from "react-select";
import { IoMdClose } from "react-icons/io";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import FixedSave from "../../../../components/Actions/FixedSave";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { get, post } from "../../../../utils/helpers/api";
import { toast } from "react-toastify";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import CustomSelect from "../../../../components/Forms/CustomSelect";
import { useTranslation } from "react-i18next";
import DefaultButton from "../../../../components/Buttons/Default";
import TaxModal from "./Modals/Tax";
import DiscountModal from "./Modals/Discount";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { useSettings } from "../../../../context/settings";

const calcRowTotal = (row) => {
  const quantity = parseFloat(row.quantity) || 0;
  const unitPrice = parseFloat(row.unitPrice) || 0;
  const taxRate = parseFloat(row.taxRate) || 0;
  const discountRate = parseFloat(row.discountRate) || 0;
  const base = quantity * unitPrice;

  return base - (base * discountRate / 100) + (base * taxRate / 100);
};

function CreateQuotePage() {
  const { t } = useTranslation();
  const { datePickerFormat, updateSettings } = useSettings();
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlCustomerId = searchParams.get('customer_id') ? searchParams.get('customer_id'): null;
  const [initLoading, setInitLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [customersData, setCustomersData] = useState([]);
  const [employeesData, setEmployeesData] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  const [taxModalOpen, setTaxModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [quoteData, setQuoteData] = useState({
    quote_for: state?.quoteData?.quote_for || 'customer', // customer or lead
    lead: state?.quoteData?.lead ? { value: state.quoteData.lead_id, label: state.quoteData?.lead?.name } : null,
    lead_id: state?.quoteData?.lead_id || null,
    customer: state?.quoteData?.customer ? { value: state.quoteData.customer_id, label: state.quoteData?.customer?.company_name } : null,
    customer_id: state?.quoteData?.customer_id || urlCustomerId || "",
    quote_number: state?.quoteData?.quote_number || "",
    quote_date: state?.quoteData?.quote_date ? parseISO(state.quoteData.quote_date) : new Date(),
    expiry_date: state?.quoteData?.expiry_date ? parseISO(state.quoteData.expiry_date) : new Date(new Date().setDate(new Date().getDate() + 15)),
    sales_person_id: state?.quoteData?.sales_person_id || null,
    tax_rate: state?.quoteData?.tax_rate || 0,
    discount_rate: state?.quoteData?.discount_rate || 0,
    discount_type: state?.quoteData?.discount_type || 'percent',
    notes: state?.quoteData?.notes || '',
  });
  const [rows, setRows] = useState(
    state?.quoteData?.items?.length > 0 ? state.quoteData.items.map((item, index) => ({
      id: Date.now() + index,
      product_name: item.product_name,
      product_id: item.product_id,
      description: item.description,
      quantity: item.quantity,
      unitPrice: item.unit_price,
      taxRate: item.tax_rate || 0,
      discountRate: item.discount_rate || 0,
      total: calcRowTotal({
        quantity: item.quantity,
        unitPrice: item.unit_price,
        taxRate: item.tax_rate,
        discountRate: item.discount_rate,
      })
    })) : [{
      id: Date.now(),
      product_name: '',
      product_id: null,
      description: '',
      quantity: 1,
      unitPrice: 0,
      taxRate: 0,
      discountRate: 0,
      total: 0
    }]
  );

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    setInitLoading(true);
    const data = await get('/sales/quotes/create');

    if(data.success) {
      setCustomersData(data.customers);
      setEmployeesData(data.employees);
      setCompanySettings(data.company_settings);
      updateSettings(data.company_settings);
      if(quoteData?.quote_number === "") {
        setQuoteData(prev => ({
          ...prev,
          quote_number: data.quote_number
        }));
      }
      // check for tax
      if(!quoteData.tax_rate && data.company_settings?.sales?.default_tax_rate) {
        setQuoteData(prev => ({
          ...prev,
          tax_rate: data.company_settings?.sales?.default_tax_rate || 0
        }));
      }

      setInitLoading(false);
    }
  }

  const handleAddRow = () => {
    setRows([...rows, {
      id: Date.now(),
      product_name: '',
      product_id: null,
      description: '',
      quantity: 1,
      unitPrice: 0,
      taxRate: 0,
      discountRate: 0,
      total: 0
    }]);
  };

  const handleDeleteRow = (id) => {
    setRows(rows.filter(row => row.id !== id));
  };

  const saveQuote = async () => {
    setSaveLoading(true);

    const url = id ? '/sales/quotes/update/' + id : '/sales/quotes/store';
    const data = await post(url, {
      ...quoteData,
      quote_date: quoteData.quote_date ? format(quoteData.quote_date, 'yyyy-MM-dd') : null,
      expiry_date: quoteData.expiry_date ? format(quoteData.expiry_date, 'yyyy-MM-dd') : null,
      items: rows
    });

    if(data.success) {
      navigate('/sales/quotes');
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  }

  const calcPrices = () => {
    const taxRate = quoteData.tax_rate || 0;
    const discountRate = quoteData.discount_rate || 0;
    const subTotal = rows.reduce((acc, row) => acc + (parseFloat(row.total) || 0), 0);
    const discountAmount = function() {
      if(quoteData.discount_type === 'percent') {
        return subTotal * discountRate / 100;
      } else {
        return discountRate;
      }
    };
    const taxAmount = (subTotal - discountAmount()) * taxRate / 100;
    const total = subTotal - discountAmount() + taxAmount;

    return {
      subTotal, 
      discountAmount: discountAmount(),
      taxAmount, 
      total
    };
  }

  const updateRow = (key, value) => {
    setRows(prev => prev.map(row => {
      if(row.id !== key) return row;
      const updated = { ...row, ...value };
      return { ...updated, total: calcRowTotal(updated) };
    }));
  };

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('sales.quotes.title'), id ? t('sales.editQuote') : t('sales.newQuote')]}
          onBack={() => navigate('/sales/quotes')}
        />
        <div className="subheader-page-content scrollable">
          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.client')}: </Label>
              <Row className="g-1">
                <Col xs="4">
                  <Input type="select" value={quoteData.quote_for} onChange={e => setQuoteData({ ...quoteData, quote_for: e.target.value, customer_id: null, lead_id: null })}>
                    <option value="customer">{t('crm.customer')}</option>
                    <option value="lead">{t('crm.lead')}</option>
                  </Input>
                </Col>
                <Col xs="8">
                  {quoteData.quote_for === 'lead' ? (
                    <CustomSelect
                      value={quoteData.lead || null}
                      onChange={selected => setQuoteData({ ...quoteData, lead: selected, lead_id: selected ? selected.value : null })}
                      module="crm/leads"
                      placeholder={t('crm.searchLeadPlaceholder')}
                      createLink="/crm/leads/create"
                    />
                  ) : (
                    <CustomSelect
                      value={quoteData.customer || null}
                      onChange={selected => setQuoteData({ ...quoteData, customer: selected, customer_id: selected ? selected.value : null })}
                      module="crm/customers"
                      placeholder={t('sales.searchCustomer')}
                      createLink="/crm/customers/create"
                    />
                  )}
                </Col>
              </Row>
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.quoteNumber')}</Label>
              <Input
                type="text"
                value={quoteData.quote_number}
                readOnly
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.quoteDate')}</Label>
              <DatePicker
                selected={quoteData.quote_date}
                onChange={(d) => setQuoteData({ ...quoteData, quote_date: d })}
                className="form-control"
                dateFormat={datePickerFormat}
              />
            </Col>
            <Col md="6" className="mb-3">
              <Label>{t('sales.expiry')}</Label>
              <DatePicker
                selected={quoteData.expiry_date}
                onChange={(d) => setQuoteData({ ...quoteData, expiry_date: d })}
                className="form-control"
                dateFormat={datePickerFormat}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.salesperson')}: </Label>
              <Select
                options={employeesData.map(employee => ({ value: employee.id, label: employee.firstname + ' ' + employee.lastname }))}
                styles={SelectCustomStyles}
                value={employeesData.map(e => ({ value: e.id, label: e.firstname + ' ' + e.lastname })).find(o => o.value === quoteData.sales_person_id) || null}
                onChange={selected => setQuoteData({ ...quoteData, sales_person_id: selected ? selected.value : null })}
              />
            </Col>
          </Row>

          <Table bordered>
            <thead>
              <tr>
                <th>{t('common.item')}</th>
                <th width="100px">{t('common.quantity')}</th>
                <th width="120px">{t('common.unitPrice')}</th>
                <th width="120px">{t('sales.taxPercent')}</th>
                <th width="120px">{t('sales.discountPercent')}</th>
                <th width="140px">{t('common.total')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((data) => (
                <tr key={data.id}>
                  <td style={{minWidth: 200}}>
                    <CustomSelect
                      value={data.product_name ? { value: data.product_id, label: data.product_name } : null}
                      onChange={(selected, objectData) => {
                        updateRow(data.id, {
                          product_id: selected ? selected.value : null,
                          product_name: selected ? selected.label : '',
                          unitPrice: objectData?.selling_price ?? data.unitPrice,
                        });
                      }}
                      module="inventory/products"
                      placeholder={t('inventory.searchProduct')}
                      createLink="/inventory/products/create"
                      canCreate
                    />
                    <Input
                      type="textarea"
                      rows={1}
                      value={data.description}
                      onChange={e => updateRow(data.id, { description: e.target.value })}
                      placeholder={t('common.description')}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={data.quantity}
                      onChange={e => updateRow(data.id, { quantity: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={data.unitPrice}
                      onChange={e => updateRow(data.id, { unitPrice: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={data.taxRate || 0}
                      onChange={e => updateRow(data.id, { taxRate: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={data.discountRate || 0}
                      onChange={e => updateRow(data.id, { discountRate: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={data.total}
                      readOnly
                    />
                  </td>
                  <td className="text-center">
                    {rows?.length > 1 &&
                      <IoMdClose onClick={() => handleDeleteRow(data.id)} />
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Row>
            <Col lg="7" md="6">
              <Row className="mb-3 g-2">
                <Col md="4">              
                  <DefaultButton 
                    color="primary" 
                    outline size="sm" 
                    onClick={handleAddRow}
                    block
                  >
                    + {t('common.addNewRow')}
                  </DefaultButton>
                </Col>
                <Col md="4">
                  <DefaultButton
                    color="success"
                    outline
                    size="sm"
                    block
                    onClick={() => setDiscountModalOpen(true)}
                  >
                    + {t('sales.addDiscount')}
                  </DefaultButton>
                </Col>
                <Col md="4">
                  <DefaultButton
                    color="light"
                    outline
                    size="sm"
                    block
                    onClick={() => setTaxModalOpen(true)}
                  >
                    + {t('sales.editTax')}
                  </DefaultButton>
                </Col>
              </Row>

              <FormGroup>
                <Label>{t('sales.notes')}</Label>
                <Input
                  type="textarea"
                  value={quoteData.notes}
                  onChange={e => setQuoteData({ ...quoteData, notes: e.target.value })}
                />
              </FormGroup>
            </Col>

            <Col lg="5" md="6">
              <div className="bg-white p-3">
                <Row className="mb-2">
                  <Col md="6">
                    <strong>{t('common.subTotal')}: </strong>
                  </Col>
                  <Col md="6" className="text-end">
                    { calcPrices().subTotal.toFixed(2) } {' '}
                    { companySettings?.sales?.base_currency }
                  </Col>
                </Row>

                {quoteData.discount_rate > 0 &&
                  <Row className="mb-2 text-danger">
                    <Col md="6">
                      <strong>
                        {t('sales.discountWithRate')}: 
                        ({ quoteData.discount_rate }
                        { quoteData?.discount_type === 'percent' ? '%' : '' })
                      </strong>
                    </Col>
                    <Col md="6" className="text-end">
                      -{ calcPrices().discountAmount.toFixed(2) } {' '}
                      { companySettings?.sales?.base_currency }
                    </Col>
                  </Row>
                }

                <Row className="mb-3">
                  <Col md="6">
                    <strong>{t('sales.taxWithRate', { rate: quoteData.tax_rate })}: </strong>
                  </Col>
                  <Col md="6" className="text-end">
                    { calcPrices().taxAmount.toFixed(2) } {' '}
                    { companySettings?.sales?.base_currency }
                  </Col>
                </Row>

                <hr />

                <Row>
                  <Col md="6">
                    <h5 className="fw-bold mb-0">{t('common.total')}: </h5>
                  </Col>
                  <Col md="6" className="text-end">
                    { calcPrices().total.toFixed(2) } {' '}
                    { companySettings?.sales?.base_currency }
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <TaxModal 
        isOpen={taxModalOpen} 
        toggle={() => setTaxModalOpen(false)} 
        taxRate={quoteData.tax_rate}
        onTaxRateChange={(newTaxRate) => setQuoteData(prev => ({ ...prev, tax_rate: newTaxRate }))}
      />

      <DiscountModal 
        isOpen={discountModalOpen} 
        toggle={() => setDiscountModalOpen(false)} 
        discountRate={quoteData.discount_rate}
        onDiscountRateChange={(newDiscountRate) => setQuoteData(prev => ({ ...prev, discount_rate: newDiscountRate }))}
        discountType={quoteData.discount_type}
        onDiscountTypeChange={(newDiscountType) => setQuoteData(prev => ({ ...prev, discount_type: newDiscountType }))}
      />

      <FixedSave
        onSave={saveQuote}
        loading={saveLoading}
        disabled={saveLoading || (!quoteData?.customer_id && !quoteData?.lead_id) || rows.length === 0}
      />
    </SidebarLayout>
  );
}

export default CreateQuotePage;
